import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	getBilletWorkOrders,
	getBilletWorkOrdersPickers,
	upsertBilletWorkOrder,
	type GetBilletWorkOrdersParams,
	type GetBilletWorkOrdersPickersParams,
	type UpsertBilletWorkOrderParams,
} from "../repositories/BilletWorkOrdersRepository";

type UseBilletWorkOrdersParams = Omit<GetBilletWorkOrdersParams, "signal"> & {
	enabled?: boolean;
};

export const useBilletWorkOrders = ({
	start,
	end,
	workOrderId = null,
	billetSizeId = null,
	crewId = null,
	productSizeId = null,
	productTypeId = null,
	enabled = true,
}: UseBilletWorkOrdersParams) => {
	return useQuery({
		queryKey: [
			"billetWorkOrders",
			start,
			end,
			workOrderId,
			billetSizeId,
			crewId,
			productSizeId,
			productTypeId,
		],
		queryFn: ({ signal }) =>
			getBilletWorkOrders({
				start,
				end,
				workOrderId,
				billetSizeId,
				crewId,
				productSizeId,
				productTypeId,
				signal,
			}),
		enabled: enabled && !!start && !!end,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

type UseBilletWorkOrdersPickersParams = Omit<
	GetBilletWorkOrdersPickersParams,
	"signal"
>;

export const useBilletWorkOrdersPickers = ({
	start,
	end,
}: UseBilletWorkOrdersPickersParams) => {
	return useQuery({
		queryKey: ["billetWorkOrdersPickers", start, end],
		queryFn: ({ signal }) => getBilletWorkOrdersPickers({ start, end, signal }),
		enabled: !!start && !!end,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

interface UseUpsertBilletWorkOrderProps {
	onSuccess?: () => void;
	onError?: (err: Error) => void;
}

export const useUpsertBilletWorkOrder = ({
	onSuccess,
	onError,
}: UseUpsertBilletWorkOrderProps = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (params: UpsertBilletWorkOrderParams) =>
			upsertBilletWorkOrder(params),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["billetWorkOrders"] });
			onSuccess?.();
		},
		onError: (err: Error) => onError?.(err),
	});
	return {
		upsertBilletWorkOrder: mutation.mutate,
		isUpserting: mutation.isPending,
	};
};
